import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { EIoTProviders, IotModal } from '@treedis/tour-loader';
import { IotTourModalProps } from '@/components/iot/IotTourModal/types';
import PublicAPI from '@/api/TreedisPublicDAO';

export const IotTourModal: FC<IotTourModalProps> = ({
  open,
  setOpen,
  tag,
  providers,
  moveToTagIO,
  handleUnlock,
  iotDevices,
  openCamera,
  openFavouriteCameras,
  newDoorNotification,
  handleMoveToLock,
  tags,
}) => {
  const [selectedCameras, setSelectedCameras] = useState<{ url: string; id: null | number; name: string }[]>(Array(9).fill({ url: '', id: null, name: '' }));
  const [notificationView, setNotificationView] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<null | number>(null);
  const rombusProvider = useMemo(() => providers?.find((provider) => provider?.provider?.name === EIoTProviders.RHOMBUS), [providers]);
  const kisiProvider = useMemo(() => providers?.find((provider) => provider?.provider?.name === EIoTProviders.Kisi), [providers]);
  const tankLogixProvider = useMemo(() => providers?.find((provider) => provider?.provider?.name === EIoTProviders.TANKLOGIX), [providers]);
  const cameras = useMemo(() => iotDevices?.filter((d) => d.providerId === rombusProvider?.providerId), [iotDevices, rombusProvider]);
  const doors = useMemo(() => iotDevices?.filter((d) => d.providerId === kisiProvider?.providerId), [iotDevices, kisiProvider]);
  const engines = useMemo(() => iotDevices?.filter((d) => d.providerId === tankLogixProvider?.providerId), [tankLogixProvider, iotDevices]);
  const unshownDevices = useMemo(() => cameras?.filter((device) => !selectedCameras.find((camera) => camera.id === device.id)), [cameras, selectedCameras]);

  const relatedTag = useMemo(() => {
    const cameraTag = openCamera?.relatedAssets?.find((a) => a.type === 'tag');
    if (cameraTag) {
      const foundTag = tags?.find((t) => t.sid === cameraTag?.id || t.defaultSid === cameraTag?.id);

      const doorsRelatedToTag = doors?.filter((door) =>
        foundTag?.iotDevices?.some((device) =>
          // eslint-disable-next-line eqeqeq
          device.relatedAssets?.some((asset) => asset.id == door.id),
        ),
      );
      return { ...foundTag, iotDevices: [...foundTag.iotDevices, ...doorsRelatedToTag] };
    }
    return null;
  }, [tags, openCamera, doors]);

  const handleCameraSelect = useCallback(
    async (camera: any) => {
      const result = !camera.videoUrl
        ? await PublicAPI.getStream({
            query: {
              providerId: camera.providerId as number,
              deviceId: camera.id,
              tourId: camera.tourId,
            },
          })
        : { data: { sharedLiveVideoStreamUrl: camera.videoUrl } };

      if (result?.data?.sharedLiveVideoStreamUrl) {
        setSelectedCameras((prevCameras) => {
          return prevCameras?.map((prevCamera, i) => (i === selectedIndex ? { id: camera.id, url: result?.data?.sharedLiveVideoStreamUrl, name: camera.name } : prevCamera));
        });
      }
    },
    [selectedIndex],
  );

  useEffect(() => {
    if (tag && !openCamera && !openFavouriteCameras && !notificationView) {
      setSelectedCameras((prevCameras) => {
        const tagCameras = tag?.iotDevices?.filter((d) => d.videoUrl);
        return prevCameras.map((_, i) => {
          return {
            id: tagCameras?.[i]?.id || null,
            url: tagCameras?.[i]?.videoUrl || '',
            name: tagCameras?.[i]?.name || '',
          };
        });
      });
      return;
    }
    if (cameras?.length > 0 && openCamera && !openFavouriteCameras && !notificationView) {
      setSelectedCameras((prevCameras) => {
        return prevCameras?.map((prevCamera, i) => {
          if (i === 0) {
            const cameraOpenFromMenu = cameras?.find((camera) => camera.id === openCamera?.id);
            return {
              id: cameraOpenFromMenu ? cameraOpenFromMenu?.id : cameras[0].id,
              url: cameraOpenFromMenu ? cameraOpenFromMenu?.videoUrl : cameras[0].videoUrl,
              name: cameraOpenFromMenu ? cameraOpenFromMenu?.name : cameras[0].name,
            };
          }
          return prevCamera;
        });
      });
      return;
    }
    if (cameras && cameras?.length > 0 && openFavouriteCameras?.cameras?.length > 0 && !openCamera && !notificationView) {
      const favouriteCameras = cameras
        ?.filter((camera) => openFavouriteCameras?.cameras?.includes(camera.id))
        ?.map((camera) => ({ id: camera.id || null, url: camera.videoUrl || '' }));
      setSelectedCameras([...favouriteCameras, ...Array(9 - favouriteCameras.length).fill({ url: '', id: null, name: '' })]);
    }
  }, [cameras, openCamera, openFavouriteCameras, notificationView, tag]);

  const handleClose = () => {
    setOpen(false, false);
    setNotificationView(false);
    setSelectedCameras(Array(9).fill({ url: '', id: null, name: '' }));
  };

  const handleOpenVideo = useCallback((camera) => {
    setSelectedCameras((prevCameras) => {
      return prevCameras.map((prevCamera, i) => (i === 0 ? { id: camera.id, url: camera.videoUrl, name: camera.name } : prevCamera));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoFromNotifications = useCallback((devices) => {
    setNotificationView(true);
    if (devices.length === 1) {
      setSelectedCameras((prevCameras) => {
        return prevCameras.map((prevCamera, i) => (i === 0 ? { id: devices[0].id, url: devices[0].videoUrl, name: devices[0].name } : prevCamera));
      });
    }
    if (devices.length > 1) {
      setSelectedCameras((prevCameras) => {
        return prevCameras.map((prevCamera, i) => {
          return { id: devices[i]?.id || prevCamera.id, url: devices[i]?.videoUrl || prevCamera.url, name: devices[i]?.name || prevCamera.name };
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeCameraFromGrid = (camera) => {
    setSelectedCameras((prevCameras) => {
      return prevCameras.map((prevCamera) => {
        if (prevCamera.id === camera.id) {
          return { id: null, url: '', name: '' };
        }
        return prevCamera;
      });
    });
  };

  return (
    <IotModal
      open={open}
      handleClose={handleClose}
      tag={tag || relatedTag}
      providers={providers}
      setSelectedIndex={setSelectedIndex}
      selectedCameras={selectedCameras}
      handleCameraSelect={handleCameraSelect}
      unshownDevices={unshownDevices}
      moveToTag={moveToTagIO}
      handleUnlock={handleUnlock}
      cameras={cameras}
      doors={doors}
      handleOpenVideo={handleOpenVideo}
      bottomBarWidth={undefined}
      windowWidth={undefined}
      // modalStyle={{ top: '-94px' }}
      reelStyle={{ left: 0 }}
      favouriteView={openFavouriteCameras}
      newDoorNotification={newDoorNotification}
      handleMoveToLock={handleMoveToLock}
      openVideoFromNotifications={openVideoFromNotifications}
      removeCameraFromGrid={removeCameraFromGrid}
      engines={engines}
    />
  );
};
